/**
 * Licenses & Certifications Page
 * Track license renewals, certifications, and compliance documents
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, AlertTriangle, CheckCircle, Clock, Calendar, Upload,
  Building2, ExternalLink, Download, Bell, Shield
} from 'lucide-react';
import { licenses, documentCategories, complianceTasks, getExpiringLicenses, getExpiredLicenses, getOverdueTasks } from '@/data/compliance';
import { cn } from '@/lib/utils';

export function LicensesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Filter licenses
  const filteredLicenses = useMemo(() => {
    let filtered = licenses;
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(l => l.type === categoryFilter);
    }
    return filtered;
  }, [categoryFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: licenses.length,
    active: licenses.filter(l => l.status === 'active').length,
    expiring: getExpiringLicenses(60).length,
    expired: getExpiredLicenses().length,
    overdueTasks: getOverdueTasks().length,
  }), []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-spice-cardamom';
      case 'expiring-soon': return 'bg-spice-turmeric';
      case 'expired': return 'bg-spice-tandoori';
      default: return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'expiring-soon': return 'Utløper snart';
      case 'expired': return 'Utløpt';
      default: return status;
    }
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'licenses': return <Shield className="w-4 h-4" />;
      case 'certifications': return <CheckCircle className="w-4 h-4" />;
      case 'insurance': return <Shield className="w-4 h-4" />;
      case 'contracts': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div data-testid="licenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal">Lisenser & Sertifiseringer</h1>
          <p className="text-gray-500 text-sm mt-1">
            Spor lisensfornyelser og sertifiseringer
          </p>
        </div>
        <Button className="bg-brand-orange hover:bg-brand-orange/90 gap-2">
          <Upload className="w-4 h-4" />
          Last opp dokument
        </Button>
      </div>

      {/* Alert Banner */}
      {(stats.expired > 0 || stats.expiring > 0) && (
        <Card className={cn(
          "border-l-4",
          stats.expired > 0 ? "border-l-spice-tandoori bg-spice-tandoori/5" : "border-l-spice-turmeric bg-spice-turmeric/5"
        )}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className={cn(
                "w-6 h-6",
                stats.expired > 0 ? "text-spice-tandoori" : "text-spice-turmeric"
              )} />
              <div>
                <p className="font-medium text-brand-charcoal">
                  {stats.expired > 0 
                    ? `${stats.expired} lisens(er) har utløpt!` 
                    : `${stats.expiring} lisens(er) utløper snart`}
                </p>
                <p className="text-sm text-gray-500">
                  Gjennomgå og forny for å unngå driftsproblemer
                </p>
              </div>
            </div>
            <Button variant="outline" className="gap-2">
              <Bell className="w-4 h-4" />
              Se alle
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Totalt dokumenter</p>
                <p className="text-2xl font-bold text-brand-charcoal">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-brand-orange/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Aktive</p>
                <p className="text-2xl font-bold text-spice-cardamom">{stats.active}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-spice-cardamom/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Utløper snart</p>
                <p className="text-2xl font-bold text-spice-turmeric">{stats.expiring}</p>
              </div>
              <Clock className="w-8 h-8 text-spice-turmeric/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Utløpt</p>
                <p className="text-2xl font-bold text-spice-tandoori">{stats.expired}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-spice-tandoori/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Forfalt oppgaver</p>
                <p className="text-2xl font-bold text-spice-tandoori">{stats.overdueTasks}</p>
              </div>
              <Calendar className="w-8 h-8 text-spice-tandoori/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Filtrer:</span>
        <Button 
          variant={categoryFilter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setCategoryFilter('all')}
        >
          Alle
        </Button>
        {documentCategories.slice(0, 5).map(cat => (
          <Button 
            key={cat.id}
            variant={categoryFilter === cat.id ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setCategoryFilter(cat.id)}
            className="gap-1"
          >
            {getCategoryIcon(cat.id)}
            {cat.nameNo}
          </Button>
        ))}
      </div>

      {/* Licenses List */}
      <div className="grid gap-4">
        {filteredLicenses.map(license => {
          const daysLeft = getDaysUntilExpiry(license.expiryDate);
          const progressValue = daysLeft !== null 
            ? Math.max(0, Math.min(100, (daysLeft / 365) * 100))
            : 100;

          return (
            <Card key={license.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-lg flex items-center justify-center",
                      license.status === 'active' ? "bg-spice-cardamom/10 text-spice-cardamom" :
                      license.status === 'expiring-soon' ? "bg-spice-turmeric/10 text-spice-turmeric" :
                      "bg-spice-tandoori/10 text-spice-tandoori"
                    )}>
                      {getCategoryIcon(license.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-brand-charcoal">{license.nameNo}</h3>
                        <Badge className={cn("text-xs", getStatusColor(license.status))}>
                          {getStatusLabel(license.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{license.name}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {license.issuingAuthority}
                        </span>
                        <span>Nr: {license.licenseNumber}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {license.expiryDate ? (
                      <>
                        <p className={cn(
                          "text-sm font-medium",
                          daysLeft < 0 ? "text-spice-tandoori" :
                          daysLeft < 60 ? "text-spice-turmeric" :
                          "text-gray-600"
                        )}>
                          {daysLeft < 0 
                            ? `Utløpt for ${Math.abs(daysLeft)} dager siden`
                            : `${daysLeft} dager igjen`}
                        </p>
                        <p className="text-xs text-gray-500">Utløper: {license.expiryDate}</p>
                      </>
                    ) : (
                      <p className="text-sm text-spice-cardamom font-medium">Permanent</p>
                    )}
                  </div>
                </div>

                {/* Progress bar for expiry */}
                {license.expiryDate && (
                  <div className="mt-4">
                    <Progress 
                      value={progressValue} 
                      className={cn(
                        "h-2",
                        daysLeft < 0 ? "[&>div]:bg-spice-tandoori" :
                        daysLeft < 60 ? "[&>div]:bg-spice-turmeric" :
                        "[&>div]:bg-spice-cardamom"
                      )}
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <div className="flex items-center gap-2">
                    {license.notes && (
                      <span className="text-xs text-gray-500 italic">{license.notes}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1">
                      <Download className="w-3 h-3" /> Last ned
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1">
                      <ExternalLink className="w-3 h-3" /> Åpne
                    </Button>
                    {license.renewalRequired && (
                      <Button size="sm" className="bg-brand-orange hover:bg-brand-orange/90 gap-1">
                        <Calendar className="w-3 h-3" /> Forny
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Compliance Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-brand-orange" />
            Compliance-oppgaver
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {complianceTasks.map(task => (
              <div 
                key={task.id} 
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  task.status === 'overdue' ? "bg-spice-tandoori/5 border-spice-tandoori/20" :
                  task.status === 'due-today' ? "bg-spice-turmeric/5 border-spice-turmeric/20" :
                  "bg-gray-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    task.status === 'completed' ? "bg-spice-cardamom/20 text-spice-cardamom" :
                    task.status === 'overdue' ? "bg-spice-tandoori/20 text-spice-tandoori" :
                    "bg-spice-turmeric/20 text-spice-turmeric"
                  )}>
                    {task.status === 'completed' ? <CheckCircle className="w-4 h-4" /> :
                     task.status === 'overdue' ? <AlertTriangle className="w-4 h-4" /> :
                     <Clock className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{task.nameNo}</p>
                    <p className="text-xs text-gray-500">{task.frequency}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={cn(
                    "text-xs",
                    task.status === 'completed' ? "bg-spice-cardamom" :
                    task.status === 'overdue' ? "bg-spice-tandoori" :
                    "bg-spice-turmeric"
                  )}>
                    {task.status === 'completed' ? 'Fullført' :
                     task.status === 'overdue' ? 'Forfalt' :
                     'I dag'}
                  </Badge>
                  {task.status !== 'completed' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs">
                      Merk fullført
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
